/**
 * The Word download every tool uses: one control, two languages.
 *
 * `DE` downloads the document as the tool wrote it. `EN` asks the server for
 * the English copy (`?lang=en`): the finished document translated with GPT-6,
 * formatting kept — that takes a few seconds, and the pressed button spins
 * meanwhile. The server names the file (`HKCM_<Tool>_<Datum>[_EN].docx`);
 * `fallbackName` is used only when a response carries no name.
 *
 * Sign-in, a 401, errors and the save itself are handled here, so a page only
 * says what to export.
 */

import { FileDown, LoaderCircle } from 'lucide-react'
import { useState } from 'react'

import { useAuth } from '../../hooks/useAuth'
import { errorDetail, filenameFromDisposition, saveBlob } from '../../utils/download'
import { reportSessionExpired } from '../../utils/session'
import { toast } from '../../utils/toast'
import { cn, FOCUS_RING_INSET } from './styles'
import Tooltip from './Tooltip'

export type ExportLanguage = 'de' | 'en'

export interface WordExportRequest {
    /** The export route, without `lang`. */
    url: string
    method?: 'GET' | 'POST'
    /** Sent as JSON. */
    body?: unknown
}

interface WordExportButtonProps {
    /** What to export; null when there is nothing (the click does nothing). */
    request: () => WordExportRequest | null
    fallbackName?: string
    /** The visible name: "Word", or a variant such as "Word ohne TradingView". */
    label?: string
    disabled?: boolean
    errorMessage?: string
    size?: 'sm' | 'md'
    className?: string
}

const LANGUAGES: ReadonlyArray<{ lang: ExportLanguage; code: string; name: string; hint: string }> = [
    { lang: 'de', code: 'DE', name: 'auf Deutsch', hint: 'So, wie das Werkzeug ihn geschrieben hat' },
    { lang: 'en', code: 'EN', name: 'auf Englisch', hint: 'Mit GPT-6 ins Englische übersetzt, dauert einige Sekunden' },
]

const SIZE = {
    sm: { box: 'h-8 text-xs', label: 'pl-2.5 pr-1.5', button: 'px-2.5' },
    md: { box: 'h-10 text-sm', label: 'pl-3 pr-2', button: 'px-3' },
} as const

function withLanguage(url: string, lang: ExportLanguage): string {
    return `${url}${url.includes('?') ? '&' : '?'}lang=${lang}`
}

function localName(fallbackName: string, lang: ExportLanguage): string {
    return lang === 'en' ? fallbackName.replace(/(\.docx)?$/i, '_EN.docx') : fallbackName
}

export default function WordExportButton({
    request,
    fallbackName = 'HKCM_Export.docx',
    label = 'Word',
    disabled = false,
    errorMessage = 'Der Word-Export ist fehlgeschlagen.',
    size = 'md',
    className,
}: WordExportButtonProps) {
    const { getToken, authEnabled } = useAuth()
    const [busy, setBusy] = useState<ExportLanguage | null>(null)
    const sizes = SIZE[size]

    const download = async (lang: ExportLanguage) => {
        const target = request()
        if (!target || busy) return
        setBusy(lang)
        try {
            const token = await getToken()
            if (authEnabled && !token) {
                reportSessionExpired()
                return
            }
            const headers: Record<string, string> = {}
            if (token) headers.Authorization = `Bearer ${token}`
            if (target.body !== undefined) headers['Content-Type'] = 'application/json'
            const response = await fetch(withLanguage(target.url, lang), {
                method: target.method ?? 'POST',
                headers,
                body: target.body === undefined ? undefined : JSON.stringify(target.body),
            })
            if (response.status === 401) {
                reportSessionExpired()
                return
            }
            if (!response.ok) {
                // A 4xx with a reason ("nothing in this period") is a notice;
                // anything else is a failure.
                const detail = await errorDetail(response)
                if (detail && response.status < 500) toast.warning(detail)
                else toast.error(detail ?? errorMessage)
                return
            }
            const name =
                filenameFromDisposition(response.headers.get('Content-Disposition')) ??
                localName(fallbackName, lang)
            saveBlob(await response.blob(), name)
        } catch (err) {
            console.error('Word export failed', err)
            toast.error(errorMessage)
        } finally {
            setBusy(null)
        }
    }

    return (
        <div
            role="group"
            aria-label={`${label} herunterladen`}
            className={cn(
                'inline-flex items-stretch overflow-hidden rounded-full border border-line-strong bg-surface font-semibold text-fg shadow-xs',
                sizes.box,
                disabled && 'opacity-50',
                className,
            )}
        >
            <span className={cn('inline-flex flex-1 items-center gap-2 whitespace-nowrap', sizes.label)} aria-hidden="true">
                <FileDown className="h-4 w-4" />
                {label}
            </span>
            {LANGUAGES.map(({ lang, code, name, hint }) => (
                <Tooltip key={lang} label={hint} side="bottom" className="flex">
                    <button
                        type="button"
                        onClick={() => void download(lang)}
                        disabled={disabled || busy !== null}
                        aria-busy={busy === lang || undefined}
                        aria-label={`${label} ${name} herunterladen`}
                        className={cn(
                            'inline-flex items-center gap-1.5 border-l border-line-strong transition-colors hover:bg-surface-subtle hover:text-primary disabled:cursor-not-allowed',
                            'disabled:hover:bg-transparent disabled:hover:text-fg',
                            sizes.button,
                            FOCUS_RING_INSET,
                        )}
                    >
                        {busy === lang && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />}
                        {code}
                    </button>
                </Tooltip>
            ))}
            <span className="sr-only" aria-live="polite">
                {busy === 'en' ? 'Die englische Fassung wird übersetzt …' : busy === 'de' ? 'Word wird erstellt …' : ''}
            </span>
        </div>
    )
}
